/**
 * Admin Portal (web): "Refresh RevenueCat cohort labels" (2026-09-27).
 *
 * The owner's button for the one-time re-stamp of RevenueCat's `cedar_cohort` on expired
 * users (#3967): a lapsed PAID subscriber must be labelled `paid_expired` ("Re-subscribe to
 * continue with your coach"), not `trial_expired` ("Your 7 days with Cedar are done").
 * Preview first (counts per label; nothing written), then Apply (at most four stamps a
 * second). super_admin only — the server refuses everyone else with 403.
 *
 * Backend: routers/admin_rc_cohort_labels.py. Decisions: ./rcCohortLabelsLogic.ts.
 * Web-only (the admin portal is not part of the native app).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Tags, AlertTriangle } from 'lucide-react';
import { AdminApiError, adminFetch } from '../../utils/adminApi';
import {
  applyTarget,
  countsRows,
  latestPreview,
  runLine,
  runningRun,
  stampableUsers,
  type RestampRun,
} from './rcCohortLabelsLogic';

const BASE = '/api/admin/rc-cohort-labels';
const POLL_MS = 2000;

// Through the shared admin helper (utils/adminApi.ts: the session token, and no request
// at all without one), never a hand-rolled Authorization header (test_raw_bearer_ratchet).
async function call(path: string, init?: RequestInit): Promise<{ status: number; body: any }> {
  try {
    return { status: 200, body: await adminFetch<any>(`${BASE}${path}`, init) };
  } catch (err) {
    if (err instanceof AdminApiError) {
      let body: any = null;
      try {
        body = err.body ? JSON.parse(err.body) : null;
      } catch {
        body = null;
      }
      return { status: err.status, body };
    }
    // A non-JSON 200 is the SPA fallback page, not an answer: never read it as success.
    return { status: 502, body: null };
  }
}

function detailText(body: any, fallback: string): string {
  const d = body?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
}

export function RcCohortLabelsPanel(): React.ReactElement {
  const [runs, setRuns] = useState<RestampRun[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // False once unmounted: no poll may re-arm itself after the panel is gone.
  const mounted = useRef(true);

  const load = useCallback(async () => {
    const { status, body } = await call('/runs');
    if (!mounted.current) return [] as RestampRun[];
    if (status === 403) {
      setForbidden(true);
      return [] as RestampRun[];
    }
    if (status !== 200 || !body?.success || !Array.isArray(body.runs)) {
      setError('Could not read the runs.');
      return [] as RestampRun[];
    }
    setError(null);
    setRuns(body.runs);
    return body.runs as RestampRun[];
  }, []);

  // Poll while a run is in progress (the run executes on the server). One timer at a time,
  // and never after unmount.
  const poll = useCallback(async () => {
    const latest = await load();
    if (!mounted.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = runningRun(latest) ? setTimeout(() => void poll(), POLL_MS) : null;
  }, [load]);

  useEffect(() => {
    mounted.current = true;
    void poll();
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [poll]);

  const start = async (path: string, body?: object) => {
    setBusy(true);
    setError(null);
    try {
      const res = await call(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (!mounted.current) return;
      if (res.status !== 200 || !res.body?.success) {
        setError(detailText(res.body, `The server answered ${res.status}.`));
      }
      await poll();
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const running = runningRun(runs);
  const preview = latestPreview(runs);
  const target = applyTarget(runs);
  const shown = running ?? runs[0] ?? null;

  const onApply = () => {
    if (!target.preview) return;
    const n = stampableUsers(target.preview);
    if (!window.confirm(
      `Re-read the current expired users and stamp each one's RevenueCat label now? `
      + `The preview counted ${n}; the apply checks everyone again, so the number stamped can differ. `
      + 'A repeat stamps the same labels.',
    )) return;
    void start('/apply', { preview_run_id: target.preview.id });
  };

  return (
    <div className="mt-8 p-5 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 mb-1">
        <Tags className="h-5 w-5" style={{ color: '#1F6C4F' }} />
        <h2 className="text-lg font-semibold" style={{ color: '#0B1614' }}>Refresh RevenueCat cohort labels</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Relabels every expired user in RevenueCat so its paywall shows the right words: a lapsed paid subscriber
        sees “Re-subscribe to continue with your coach”, a finished free week “Your 7 days with Coach Cedar are done”.
        Preview first — it writes nothing — then apply: the apply re-reads the current expired users and stamps each one as it stands now.
      </p>

      {forbidden ? (
        <p className="text-sm text-gray-600">Only a super_admin can run this.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => void start('/preview')}
              disabled={busy || !!running}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Preview
            </button>
            <button
              onClick={onApply}
              disabled={busy || !target.preview}
              title={target.reason ?? undefined}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: '#1F6C4F' }}
            >
              {target.preview ? `Apply — re-read and stamp (preview counted ${stampableUsers(target.preview)})` : 'Apply'}
            </button>
            {target.reason && !running && <span className="text-xs text-gray-500">{target.reason}</span>}
            {running && <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          )}

          {shown && <p className="text-sm text-gray-800 mb-3">{runLine(shown)}</p>}

          {preview && Object.keys(preview.resolved ?? {}).length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 font-medium">Label (latest preview)</th>
                  <th className="py-1 font-medium text-right">Users</th>
                </tr>
              </thead>
              <tbody>
                {countsRows(preview).map((row) => (
                  <tr key={row.label} className="border-t border-gray-100">
                    <td className="py-1"><code className="text-xs">{row.label}</code> — {row.name}</td>
                    <td className="py-1 text-right">{row.users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default RcCohortLabelsPanel;
